import { Loadable } from '@/components/loaders';
import { Select } from '@/components/selects';
import { ISelectItem } from '@/models/select';
import { useGetAudiencesQuery } from '@/services/endpoints/audiences';
import { FC, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { WhatsappCampaignFormInput } from '../../WhatsappCampaignValidation';
import { ActionButton } from './ActionButton';
import { BaseAudience } from '@/models/audiences';
import { useTranslation } from 'react-i18next';

interface AudienceSelectionStepProps {
  handleBack: () => void;
  handleNext: () => Promise<boolean>;
  workspaceId: string;
}

export const AudienceSelectionStep: FC<AudienceSelectionStepProps> = ({ handleBack, handleNext, workspaceId }) => {
  if (!workspaceId) {
    return <div>Due to some reason, we are not able to fetch your workspace details. Please go back to the previous step and try again.</div>;
  }

  return <AudienceSelection workspaceId={workspaceId} handleBack={handleBack} handleNext={handleNext} />;
};

interface AudienceSelectionProps {
  workspaceId: string;
  handleBack: () => void;
  handleNext: () => Promise<boolean>;
}

const AudienceSelection: FC<AudienceSelectionProps> = ({ workspaceId, handleBack, handleNext }) => {
  const { data: audiences, isLoading, isError } = useGetAudiencesQuery({ workspaceId });
  const { t } = useTranslation('campaign');
  const [stepError, setStepError] = useState<string | null>(null);

  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<WhatsappCampaignFormInput>();

  const selectedAudienceId = watch('audienceId');

  const audienceSelectItems: ISelectItem[] = useMemo(() => {
    return (
      audiences?.items?.map((audience: BaseAudience) => ({
        id: audience.id,
        label: audience.name,
        description: `Schedule: ${audience.scheduleType}`,
      })) || []
    );
  }, [audiences]);

  const selectedAudience = useMemo(() => {
    return audienceSelectItems?.find((audience) => audience.id === selectedAudienceId) || null;
  }, [audienceSelectItems, selectedAudienceId]);

  const handleAudienceSelection = (value: ISelectItem) => {
    setStepError(null);
    const audience = audiences?.items?.find((a) => a.id === value.id);
    setValue('audienceId', audience?.id || '');
    trigger('audienceId');
  };

  const handleNextStep = async () => {
    setStepError(null);
    const isValid = await handleNext();
    if (!isValid) {
      setStepError(errors.audienceId?.message || 'Please select an audience to continue.');
    }
  };

  return (
    <Loadable isLoading={isLoading}>
      <div className='space-y-6 w-full'>
        {isError && (
          <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
            Unable to load audiences. Please refresh and try again.
          </div>
        )}
        {stepError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{stepError}</div>}
        <div className='space-y-6 w-full'>
          <div className='flex flex-col'>
            <div className='flex w-full justify-between gap-4'>
              <label className='mb-2 block text-sm font-medium text-gray-700'>
                {t('ALL:destinations.customConnectorAudienceSelection.available_audiences_label')}
              </label>
              <p className='shrink-0'>{audienceSelectItems?.length} audiences found</p>
            </div>
            <div className='w-full'>
              <Select
                items={audienceSelectItems}
                value={selectedAudience}
                onChange={handleAudienceSelection}
                className='w-full'
                placeholder={t('ALL:destinations.customConnectorAudienceSelection.select_audience_placeholder')}
              />
            </div>
          </div>
          {selectedAudience && (
            <div className='rounded-lg bg-blue-50 p-4'>
              <h4 className='mb-2 font-medium text-blue-900'>{t('ALL:destinations.customConnectorAudienceSelection.selected_audience_title')}</h4>
              <div className='text-sm text-blue-800'>
                <p>
                  <strong>{t('ALL:destinations.customConnectorAudienceSelection.name_label')}</strong> {selectedAudience.label}
                </p>
                <p>
                  <strong>{t('ALL:destinations.customConnectorAudienceSelection.schedule_type_label')}</strong>{' '}
                  {selectedAudience?.description?.replace('Schedule: ', '')}
                </p>
              </div>
            </div>
          )}
        </div>
        <ActionButton handleBack={handleBack} handleNext={handleNextStep} isNextDisabled={!selectedAudienceId || isError} />
      </div>
    </Loadable>
  );
};
